import { Form, Formik, useFormikContext } from 'formik';
import { useEffect } from 'react';
import { useIsMutating } from 'react-query';
import { Outlet, useNavigate } from 'react-router-dom';

import { Button } from '@hivebrite/design-system/Button';

import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import Navigation from 'hb-react/shared/apps/Events/components/EventRegistrationContainer/components/Navigation';
import type { TicketItem } from 'hb-react/shared/apps/Events/pages/Booking/hooks/useBooking';
import { useBooking } from 'hb-react/shared/apps/Events/pages/Booking/hooks/useBooking';
import { initCart } from 'hb-react/shared/apps/Events/pages/Booking/pages/TicketSelection/utils/transformer';
import { useAmplitude } from 'hb-react/shared/hooks/useAmplitude';

import { TicketTable } from './components/TicketTable';

const TicketForm = ({
  maxTicketsPerUser,
  isValid = false,
}: {
  isValid: boolean;
  maxTicketsPerUser?: number;
}) => {
  const navigate = useNavigate();
  const isMutating = useIsMutating();
  const { trackEvent } = useAmplitude();

  const { submitForm, values, dirty } = useFormikContext<{
    tickets: TicketItem[];
  }>();

  useEffect(() => {
    if (dirty) {
      submitForm();
    }
  }, [submitForm, dirty]);

  const isSubmitting = Boolean(isMutating);

  const onNextClick = () => {
    trackEvent('NetworkEvents|User selects tickets', {
      tickets_count: values.tickets.length,
    });

    navigate('../registration');
  };

  return (
    <Form>
      <TicketTable
        tickets={values.tickets}
        maxTicketsPerUser={maxTicketsPerUser}
        isLoading={isSubmitting}
      />

      <Navigation>
        {({ isFixed }) => (
          <>
            <Button
              dataTestId="button-cancel-registration"
              variant="secondary"
              mr={2}
              onClick={() => navigate('../../..')}
            >
              {s__(`NetworkEvents|Cancel registration`)}
            </Button>
            <Button
              dataTestId={`button-${
                isFixed ? 'next-step' : 'proceed'
              }-registration`}
              isDisabled={!isValid}
              isLoading={isSubmitting}
              onClick={onNextClick}
            >
              {isFixed
                ? s__('NetworkEvents|Next')
                : s__('NetworkEvents|Proceed to Registration')}
            </Button>
          </>
        )}
      </Navigation>
    </Form>
  );
};

export const TicketSelectionPage = () => {
  const { event, tickets, cart, actions } = useBooking();
  const { currentUser } = useDefaultAppContexts();

  const onSubmit = async ({ tickets = [] }: { tickets: TicketItem[] }) => {
    await actions.reset({
      cartItems: initCart(tickets, cart),
      receipt: {
        firstname: currentUser?.firstname ?? '',
        lastname: currentUser?.lastname ?? '',
        email: currentUser?.email ?? '',
      },
    });
  };

  if (tickets.length === 0) {
    return null;
  }

  return (
    <Formik
      initialValues={{
        tickets,
      }}
      enableReinitialize
      onSubmit={onSubmit}
    >
      <>
        <TicketForm
          maxTicketsPerUser={event?.max_tickets_per_user}
          isValid={
            !!(
              cart &&
              cart.cart_items.filter(
                (item) => item.line_itemable_type === 'NetworkEvents::Ticket',
              ).length > 0
            )
          }
        />
        <Outlet />
      </>
    </Formik>
  );
};
